/**
 * @file I segnali: valori che la macchina pubblica accanto alla telemetria.
 *
 * L'injector legge topic ROS che non fanno parte della telemetria - una sonda,
 * un pressostato, un flag del bus CAN - e ne scrive il valore in `inbound` con
 * `message_type: "signals"`, una riga per emissione e tutti i valori dentro.
 *
 * E' un tipo di messaggio NUOVO, non un campo in piu dentro la telemetria:
 * una dashboard che non lo conosce lo ignora, mentre un payload di telemetria
 * cambiato l'avrebbe rotta. Questo file e' il lato che invece lo conosce.
 *
 * Il formato e' definito in `injector/signals_publisher.go` (SignalsDTO) e
 * documentato in `injector/signals.example.yaml`.
 */

/** Un valore nel payload, con quello che serve per sapere se fidarsi. */
export interface SignalValue {
  /**
   * Il valore letto, oppure null quando e' scaduto (vedi `stale`) o quando il
   * sensore non ha ancora pubblicato niente.
   */
  value: number | null;
  /** Unita di misura, come scritta nel file dei segnali. Solo per il testo. */
  unit?: string;
  /**
   * Quando il SENSORE ha pubblicato questo valore, non quando la riga e' stata
   * scritta. Fra due letture si ripubblica l'ultimo valore noto, quindi senza
   * questo non si distinguerebbe un dato fresco da uno fermo da dieci minuti.
   */
  at?: string;
  /** Vero quando il valore e' piu vecchio del `max_age` del suo segnale. */
  stale?: boolean;
  /**
   * Vero quando il valore e' un interruttore: 0 e 1 sono spento e acceso, non
   * due numeri.
   *
   * Lo dichiara la macchina, che sa da dove viene il segnale - un topic
   * std_msgs/Bool, o un campo di telemetria booleano. Non si deduce dal valore:
   * una temperatura di un grado vale 1 come un motore acceso, e chi indovinasse
   * scriverebbe "Acceso" sotto una sonda.
   */
  bool?: boolean;
}

/** Il payload di una riga `signals`. */
export interface SignalsPayload {
  emitted_at: string;
  /** "telemetry" quando la riga accompagna un record di telemetria. */
  sync?: string;
  /**
   * Il timestamp del messaggio di telemetria che questa riga accompagna: e' la
   * chiave con cui le due serie si appaiano esattamente, invece che per
   * vicinanza temporale.
   */
  telemetry_timestamp?: number;
  values: Record<string, SignalValue>;
}

/**
 * Gli id dei segnali che la dashboard sa collegare a un widget.
 *
 * **Devono coincidere con il campo `id:` in `signals.yaml` sulla macchina.**
 * E' l'unico punto di contatto fra le due parti: rinominare un segnale li
 * significa aggiornare questa mappa, e finche' non lo si fa il widget torna a
 * mostrare "--" senza errori da nessuna parte.
 *
 * Un segnale non elencato qui non e' ignorato: resta in `asset.signals` e lo si
 * puo leggere per id, semplicemente non ha un widget dedicato.
 */
export const SIGNAL_IDS = {
  /** Temperatura del motore (/can/get/motorTemp). */
  coolantTemp: ['motor_temp', 'motorTemp', 'coolant_temp', 'water_temp'],
  /** Temperatura dell'olio idraulico (/can/get/hydraulicOilTemp). */
  hydraulicOilTemp: ['hydraulic_oil_temp', 'hydraulicOilTemp', 'oil_temp'],
  /** Coppia motore (/can/get/motorTorque). */
  engineTorque: ['motor_torque', 'motorTorque', 'engine_torque'],
  /** Riserva carburante: flag booleano (/can/get/fuelReserveWarn). */
  fuelReserveWarn: ['fuel_reserve_warn', 'fuelReserveWarn', 'fuel_reserve'],
  /** Ore motore, contatore progressivo in ore. */
  engineHours: ['engine_hours', 'engineHours', 'motor_hours'],
} as const;

/**
 * Il prefisso con cui un segnale entra nelle proprieta esportate.
 *
 * Serve a due cose: tenere separato lo spazio dei nomi - un segnale chiamato
 * `engine_rpm` non deve sovrascrivere il campo omonimo della telemetria - e
 * permettere a chi formatta di riconoscere un segnale senza un elenco da
 * aggiornare a ogni macchina.
 */
export const SIGNAL_EXPORT_PREFIX = 'signal.';

/**
 * Gli id che la dashboard sa gia essere interruttori, anche se la macchina non
 * lo dichiara.
 *
 * Normalmente e' l'injector a dirlo, con `bool: true` accanto al valore: lui sa
 * che il topic e' std_msgs/Bool, noi da un 1 non potremmo dedurlo senza
 * sbagliare su una sonda che segna un grado. Questa lista copre il solo caso in
 * cui quella dichiarazione non arriva - una macchina con un injector piu
 * vecchio - e contiene percio unicamente gli id che questo file gia descrive
 * come flag, non un elenco di segnali da tenere aggiornato a mano.
 */
export const BOOLEAN_SIGNAL_IDS: readonly string[] = SIGNAL_IDS.fuelReserveWarn;

/**
 * L'etichetta di ripiego per un segnale che nessuna traduzione nomina:
 * `hydraulic_oil_temp` diventa `Hydraulic oil temp`.
 *
 * Non e bella quanto una traduzione, ma e cio che permette a un segnale
 * aggiunto stamattina sulla macchina di comparire in dashboard oggi, senza
 * passare da un rilascio del frontend.
 */
export function prettifySignalId(id: string): string {
  const words = id.replace(/[_.]+/g, ' ').trim();
  if (words === '') return id;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Estrae la mappa dei valori da un payload `signals`, scartando quello che non
 * ha la forma attesa.
 *
 * Tollerante di proposito: il payload arriva dal database, dove puo' esserci
 * una riga scritta da una versione piu vecchia o piu nuova dell'injector. Una
 * voce malformata viene saltata, le altre restano leggibili - il contrario
 * (buttare l'intero payload) toglierebbe anche i valori validi.
 */
export function readSignalsPayload(payload: unknown): Record<string, SignalValue> {
  if (!payload || typeof payload !== 'object') return {};

  const values = (payload as SignalsPayload).values;
  if (!values || typeof values !== 'object') return {};

  const out: Record<string, SignalValue> = {};
  for (const [id, raw] of Object.entries(values)) {
    if (!raw || typeof raw !== 'object') continue;

    const entry = raw as SignalValue;
    const value = typeof entry.value === 'number' && Number.isFinite(entry.value) ? entry.value : null;

    out[id] = {
      value,
      unit: typeof entry.unit === 'string' ? entry.unit : undefined,
      at: typeof entry.at === 'string' ? entry.at : undefined,
      stale: entry.stale === true,
      bool: entry.bool === true,
    };
  }
  return out;
}

/**
 * Il numero da mostrare per un segnale, o null quando non c'e' niente di cui
 * fidarsi.
 *
 * Accetta piu id e prende il primo presente, perche' il nome del segnale lo
 * decide chi scrive `signals.yaml` sulla macchina: la dashboard non puo'
 * imporlo, puo' solo dire quali ortografie riconosce. Se la tua non e' in
 * elenco, aggiungila a SIGNAL_IDS - e' l'unico punto da toccare.
 *
 * Un valore scaduto vale null, non l'ultimo noto: un widget che continua a
 * mostrare 87 gradi mentre la sonda e' muta da un'ora dice una cosa falsa, e la
 * dice con la stessa faccia con cui direbbe il vero. Meglio "--".
 */
export function signalNumber(
  signals: Record<string, SignalValue> | undefined,
  ids: readonly string[],
): number | null {
  if (!signals) return null;
  for (const id of ids) {
    const entry = signals[id];
    if (!entry) continue;
    if (entry.stale) return null;
    return entry.value;
  }
  return null;
}

/**
 * Lettura booleana di un segnale: i booleani viaggiano come 0 e 1, perche'
 * all'edge una soglia su un flag e' una soglia come le altre.
 *
 * Restituisce null quando il segnale manca o e' scaduto, cosi chi lo mostra
 * puo' distinguere "spento" da "non lo so" - e non accendere un avviso di
 * riserva perche' il sensore tace.
 */
export function signalFlag(
  signals: Record<string, SignalValue> | undefined,
  ids: readonly string[],
): boolean | null {
  const value = signalNumber(signals, ids);
  if (value === null) return null;
  return value !== 0;
}

/**
 * L'unita di misura dichiarata per un segnale, quando c'e'. Serve a non
 * inventare un'unita nel widget: se la macchina non la dichiara, non si scrive.
 */
export function signalUnit(
  signals: Record<string, SignalValue> | undefined,
  ids: readonly string[],
): string | undefined {
  if (!signals) return undefined;
  for (const id of ids) {
    if (signals[id]) return signals[id].unit;
  }
  return undefined;
}
